"use client";
import Arrow from "@/app/assets/images/svg/chevron.svg";
import HeaderBurgerButton from "@/components/UI/Layout/Header/HeaderBurgerButton/HeaderBurgerButton";
import HeaderLogo from "@/components/UI/Layout/Header/HeaderLogo/HeaderLogo";
import { getTranslatedLink } from "@/components/UI/Link/TranslatedLink";
import useClickOutside from "@/hooks/useClickOutside";
import { useScopedI18n } from "@/i18n/client";
import { CategoryTree } from "@/lib/5874/types";
import clsx from "clsx";
import { usePathname, useSearchParams } from "next/navigation";
import React, { FC, useEffect, useState } from "react";
import { HeaderStoreBuilderContent } from "../../headerTypes";
import styles from "./Nav.module.scss";
import NavItem from "./NavItem/NavItem";
import { useCartContext } from "@/providers/CartContextProvider";
import StoreIndicator from "../StoreIndicator/StoreIndicator";
import { StoreRegion } from "@/lib/builder/builder.config";

interface NavProps {
	bigCommerceMenu: CategoryTree[];
	builderMenuContent: HeaderStoreBuilderContent;
	openMobileMenu: boolean;
	setOpenMobileMenu: (state: boolean) => void;
	storeIndicatorProps: StoreIndicatorProps;
}

type StoreIndicatorProps = {
	store: StoreRegion;
	lang: string;
	onClickHandler: () => void;
};

const Nav: FC<NavProps> = ({
	bigCommerceMenu,
	builderMenuContent,
	openMobileMenu = false,
	setOpenMobileMenu,
	storeIndicatorProps,
}) => {
	const { store, lang, onClickHandler } = storeIndicatorProps;

	const path = usePathname();
	const search = useSearchParams();
	const { totalItems } = useCartContext();
	const [openedLevels, setOpenedLevels] = useState<number[]>([]);

	const scopedT = useScopedI18n("menu");
	const navRef = useClickOutside(() => {
		handleMenuClose();
	});

	useEffect(() => {
		// close menu on path change
		setOpenedLevels([]);
	}, [path, search]);

	const handleMenuClick = (level: number, itemId: number) => {
		if (level === 0 && openedLevels[0] === itemId) {
			handleMenuClose();
		} else {
			setOpenedLevels((prevLevels) => [
				...[...prevLevels].splice(0, level),
				itemId,
			]);
		}
	};
	const handleMenuClose = () => {
		setOpenMobileMenu(false);
		setOpenedLevels([]);
	};

	const renderSubMenu = (parent: CategoryTree, level: number) => {
		const openedId = openedLevels.at(-1);
		const isOpen = openedLevels[level - 1] === parent.entityId;
		// const isActive = (item: any) => openedLevels[level - 1] === item.entityId;
		const activeState = (item: CategoryTree) =>
			openedLevels.some((i) => i === item.entityId);

		return (
			<div
				className={clsx([
					styles.subMenu,
					level > 1 ? styles.subSubLevel : "",
					isOpen ? styles.showSubMenu : "",
					level === 1 ? styles.paddingLeft : "",
				])}
				id={`subMenu-${parent.entityId}`}
				aria-hidden={!isOpen}
			>
				<div className={clsx(styles.menuHeader, styles.subMenuHeader)}>
					<button
						type='button'
						className={styles.backButton}
						onClick={() =>
							setOpenedLevels((prevLevels) => prevLevels.slice(0, -1))
						}
					>
						<Arrow
							className={styles.backArrow}
							aria-labelledby='directional chevron'
						/>
						{scopedT("back")}
					</button>
					<HeaderBurgerButton
						isOpen
						ariaLabel='Close main navigation menu'
						onButtonClick={handleMenuClose}
					/>
				</div>
				<div className={styles.items}>
					{parent.children.map((item: any) => (
						<div key={item.entityId}>
							<NavItem
								text={item.name}
								link={item.path}
								isActive={activeState(item)}
								children={item.children}
								handleMenuClick={() => handleMenuClick(level, item.entityId)}
								handleCloseClick={handleMenuClose}
								aria-expanded={
									item.children.length > 0
										? openedId === item.entityId
										: undefined
								}
								aria-controls={
									item.children ? `subMenu-${item.entityId}` : undefined
								}
								tabIndex={isOpen}
								ariaHidden={false}
							/>
							{item.children?.length > 0 && renderSubMenu(item, level + 1)}
						</div>
					))}
					<NavItem
						text={scopedT("viewAll")}
						link={parent.path}
						isActive={false}
						tabIndex={isOpen}
						ariaHidden={false}
						handleCloseClick={handleMenuClose}
					/>
				</div>
			</div>
		);
	};

	return (
		<nav ref={navRef} className={clsx([styles.nav])}>
			<div
				className={clsx([styles.topMenu, openMobileMenu ? styles.open : ""])}
			>
				<div className={styles.menuHeader}>
					<HeaderLogo
						url={builderMenuContent?.logo?.url}
						altText={builderMenuContent?.logo?.altText}
					/>
					<HeaderBurgerButton
						isOpen
						ariaLabel='Close main navigation menu'
						onButtonClick={() => handleMenuClose()}
					/>
				</div>
				<div className={styles.top}>
					{bigCommerceMenu.map((item: any) => (
						<div key={item.entityId}>
							<NavItem
								text={item.name}
								link={item.path}
								isTopLevel={true}
								isActive={openedLevels[0] === item.entityId}
								children={item.children}
								tabIndex={true}
								aria-expanded={openedLevels[0] === item.entityId}
								aria-controls={`subMenu-${item.entityId}`}
								handleMenuClick={() => handleMenuClick(0, item.entityId)}
								handleCloseClick={handleMenuClose}
								ariaHidden={false}
							/>
							{item.children && renderSubMenu(item, 1)}
						</div>
					))}
					{builderMenuContent.menuItems.map(({ text, link }) => (
						<NavItem
							key={link}
							text={text}
							link={link}
							isActive={false}
							isTopLevel={true}
							tabIndex={true}
							ariaHidden={false}
							handleCloseClick={handleMenuClose}
						/>
					))}
				</div>
				<div className={styles.bottom}>
					<NavItem
						text={scopedT("account")}
						link={getTranslatedLink({ href: "login" })}
						mobileOnly={true}
						staticLinkType={"Account"}
						isActive={false}
						tabIndex={true}
						ariaHidden={false}
						handleCloseClick={handleMenuClose}
					/>
					<NavItem
						text={`${scopedT("cart")} (${totalItems})`}
						link={getTranslatedLink({ href: "cart" })}
						mobileOnly={true}
						isActive={false}
						staticLinkType={"Cart"}
						tabIndex={true}
						ariaHidden={false}
						handleCloseClick={handleMenuClose}
					/>
					<StoreIndicator
						store={store}
						lang={lang}
						onClickHandler={onClickHandler}
					/>
				</div>
			</div>
		</nav>
	);
};

export default Nav;
